import {
  AiSuggestionCustomResolverOptions,
  ChunkHtmlOptions,
  HtmlChunk,
  Suggestion,
} from './types'
import { Editor } from '@tiptap/core'

/**
 * A simple hash function to create a unique ID for a chunk of HTML.
 */
export const generateHash = (str: string): string => {
  let hash = 0
  for (let i = 0; i < str.length; i++) {
    const char = str.charCodeAt(i)
    hash = (hash << 5) - hash + char
    hash |= 0 // Convert to 32bit integer
  }
  return hash.toString()
}

/**
 * A placeholder for the default resolver. The real implementation would make a
 * call to an AI service.
 */
export const defaultResolver = async (
  _options: AiSuggestionCustomResolverOptions,
): Promise<Suggestion[]> => {
  console.warn(
    'Tiptap AI Suggestion: `defaultResolver` is not implemented. Please provide a custom `resolver` in the extension options to fetch suggestions.',
  )
  return []
}

/**
 * A default implementation of the chunking function. It splits the document's
 * top-level nodes into groups of a specified size.
 */
export const defaultChunkHtmlFunction = (options: ChunkHtmlOptions): HtmlChunk[] => {
  const { html, chunkSize } = options
  const parser = new DOMParser()
  const doc = parser.parseFromString(html, 'text/html')
  const body = doc.body

  const chunks: HtmlChunk[] = []
  const children = Array.from(body.children)

  for (let i = 0; i < children.length; i += chunkSize) {
    const chunkElements = children.slice(i, i + chunkSize)
    const chunkHtml = chunkElements.map(el => el.outerHTML).join('')
    if (chunkHtml.trim()) {
      chunks.push(chunkHtml)
    }
  }

  return chunks
}

/**
 * A utility to reconstruct the full HTML from an array of chunks.
 */
export const getHtmlFromChunks = (chunks: HtmlChunk[]): string => {
  return chunks.join('')
} 

export function getNextWord(editor: Editor, pos: number) {
  const text = editor.state.doc.textBetween(pos, pos + 50, ' ', '\n')
  const match = text.match(/^(\s*)(\S+)(\s*)(.*)/)

  if (match) {
    const [_, _whitespace, word, _trailingWhitespace, rest] = match
    const punctuationMatch = rest.match(/^[.,;!?:\\/]/)
    const punctuation = punctuationMatch ? punctuationMatch[0] : ''

    return {
      nextWord: word,
      punctuationMark: punctuation,
    }
  }

  return { nextWord: '', punctuationMark: '' }
}

export function getPreviousWord(editor: Editor, pos: number) {
  const text = editor.state.doc.textBetween(Math.max(0, pos - 50), pos, ' ', '\n')
  const match = text.match(/(.*)(\s+)(\S+)(\s*)$/)

  if (match) {
    const [_, _rest, _leadingWhitespace, word, _whitespace] = match

    return {
      previousWord: word,
    }
  }

  return { previousWord: '' }
}
